import { useWebsite } from '../../context/WebsiteContext';
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Package, ShoppingCart, Settings,
  FileText, LogOut, Menu, X, Bell, Paintbrush, Image, Receipt, Users, MapPin, Ruler,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLayout = ({ children }) => {
  const { config: website } = useWebsite();
  const { user, token, logout, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => { if (!loading && !isAdmin) navigate('/admin/login'); }, [isAdmin, loading, navigate]);

  const handleLogout = () => { logout(); navigate('/admin/login'); };

  const navItems = [
    { name: 'Dashboard', path: '/admin', icon: LayoutDashboard },
    { name: 'Products', path: '/admin/products', icon: Package },
    { name: 'Placements', path: '/admin/placements', icon: MapPin },
    { name: 'Sizing Charts', path: '/admin/sizing-charts', icon: Ruler },
    { name: 'Orders', path: '/admin/orders', icon: ShoppingCart },
    { name: 'Contacts', path: '/admin/contacts', icon: Users },
    { name: 'Order sync', path: '/admin/order-sync', icon: ShoppingCart },
    { name: 'In-Store Quote', path: '/admin/in-store-quote', icon: Receipt },
    { name: 'Quotes (All)', path: '/admin/quotes', icon: FileText },
    { name: 'Categories', path: '/admin/categories', icon: FileText },
    { name: 'Announcements', path: '/admin/announcements', icon: Bell },
    { name: 'Design Reviews', path: '/admin/design-reviews', icon: Paintbrush },
    { name: 'Mockup Templates', path: '/admin/mockup-templates', icon: Image },
    { name: 'Website', path: '/admin/website', icon: Settings },
    { name: 'Smart Site', path: '/admin/settings/smart-site', icon: FileText },
    { name: 'Settings', path: '/admin/settings', icon: Settings },
  ];

  const isActive = (path) => location.pathname === path || (path !== '/admin' && location.pathname.startsWith(path + '/'));

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="w-8 h-8 border-4 border-[#4A90A4] border-t-transparent rounded-full animate-spin" /></div>;
  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-layout">
      {/* Mobile Header */}
      <div className="lg:hidden flex items-center justify-between p-4 bg-[#1B3A5C]">
        <img
          src={website.logo}
          alt="KYIS" className="h-10 w-auto brightness-0 invert opacity-90"
        />
        <button aria-label="Toggle navigation" aria-expanded={sidebarOpen} onClick={() => setSidebarOpen(!sidebarOpen)} className="text-white p-2">
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          fixed lg:static inset-y-0 left-0 z-50 w-64 shrink-0 max-h-screen overflow-y-auto lg:sticky lg:top-0 bg-[#1B3A5C]
          transform lg:transform-none transition-transform duration-200
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <div className="h-full flex flex-col">
            <div className="p-6 border-b border-white/10 hidden lg:block">
              <img
                src={website.logo}
                alt="KYIS" className="h-12 w-auto brightness-0 invert opacity-90"
              />
            </div>

            <nav aria-label="Admin navigation" className="flex-1 p-4 space-y-1">
              {navItems.map((item) => (
                <Link key={item.path} to={item.path} onClick={() => setSidebarOpen(false)}
                  className={`flex items-center px-4 py-3 rounded-xl transition-all text-sm ${
                    isActive(item.path)
                      ? 'bg-[#F5A623]/20 text-[#F5A623]'
                      : 'text-blue-100/80 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <item.icon className="w-5 h-5 mr-3" />
                  {item.name}
                </Link>
              ))}
            </nav>

            <div className="p-4 border-t border-white/10">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="font-medium text-sm text-white">{user?.first_name || 'Admin'}</p>
                  <p className="text-xs text-blue-200/50">{user?.email}</p>
                </div>
              </div>
              <button onClick={handleLogout}
                className="w-full flex items-center px-4 py-2 text-sm text-blue-200/50 hover:text-white hover:bg-white/5 rounded-xl transition-all"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </button>
              <Link to="/"
                className="w-full flex items-center justify-center mt-2 px-4 py-2 text-sm border border-white/15 text-blue-200/50 hover:text-white rounded-xl transition-all"
              >
                View Site
              </Link>
            </div>
          </div>
        </aside>

        {sidebarOpen && (
          <div className="fixed inset-0 bg-black/50 lg:hidden z-40" onClick={() => setSidebarOpen(false)} />
        )}

        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 min-h-screen">
          {location.pathname.startsWith('/admin/settings') && (
            <nav aria-label="Settings navigation" className="flex flex-wrap gap-2 mb-6 border-b border-gray-200 pb-3">
              {[['General', '/admin/settings'], ['Smart Site', '/admin/settings/smart-site'], ['Users', '/admin/settings/users']].map(([name, path]) => (
                <Link key={path} to={path} aria-current={location.pathname === path ? 'page' : undefined}
                  className={`px-4 py-3 rounded-lg text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#4A90A4] ${location.pathname === path ? 'bg-[#1B3A5C] text-white' : 'text-gray-600 hover:bg-gray-100'}`}>{name}</Link>
              ))}
            </nav>
          )}
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
